import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import MovieCreationRoundedIcon from '@mui/icons-material/MovieCreationRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import { useTheme } from '../../../ThemeContext';

// Import local cinematic concept art
import sb1 from '../../../assets/ChatGPT Image Aug 24, 2026, 01_52_21 PM.png';
import sb2 from '../../../assets/ChatGPT Image Aug 24, 2026, 01_54_39 PM.png';
import sb3 from '../../../assets/ChatGPT Image Aug 24, 2026, 01_49_52 PM.png';

const genres = [
  { id: 'neo-noir', label: 'Neo-Noir Crime Thriller' },
  { id: 'scifi', label: 'Sci-Fi Cyberpunk' },
  { id: 'action', label: 'High-Octane Action' },
  { id: 'drama', label: 'Intense Psychological Drama' },
];

const scriptSamples = {
  'neo-noir': `SCENE 04: EXT. OLD CANAL ALLEY - NIGHT (02:15 AM)

The torrential rain batters the corrugated iron roofs. Steam curls off the black asphalt.
DETECTIVE ARJUN (38), trenchcoat soaked, lights a half-damp match against his thumb.

Across the alley, in the shadow of the rusted billboard, stands VIKRAM (45).

ARJUN
(voice rasping over the thunder)
You weren't supposed to be in this city. You told the commissioner you were dead.

VIKRAM
(smiles, exhaling a plume of cigar smoke)
Death is a matter of paperwork in Bangalore, Arjun. The syndicate didn't kill me. They hired me.

VIKRAM slides a matte-black encrypted drive across the wet hood of an ambassador.

VIKRAM (CONT'D)
Every corrupt minister. Every offshore cut. It's all on this chip. What you do with it determines who lives till sunrise.`,

  'scifi': `SCENE 12: INT. ORBITAL OBSERVATION DECK - ZERO HOUR

The massive curved viewport frames the blue arc of Earth below, laced with glittering digital gridlines.
NEO-ENGINEER TARA (29) monitors three floating holographic telemetry streams.

The ship's sub-atomic AI, AURA (V.O.), pulses with a soft blue frequency ripple.

AURA
(calm, synthetic tone)
Quantum jump vector confirmed, Tara. However, bio-signature anomalies detected in cargo sector 07.

TARA
(freezing, fingers hovering over the kill-switch)
Sector seven is an automated seed repository, Aura. What kind of anomaly?

AURA
A heartbeat, Tara. Accelerating. 140 beats per minute. And it is speaking in ancient Malayalam dialect.`,

  'action': `SCENE 22: EXT. HIGHWAY FLYOVER - DUSK

Tires shriek as a reinforced SUV broadsides a police escort. Shards of windshield spray like diamonds in the sunset.
DEV (32) rolls out of the passenger door, tactical carbine locked to his shoulder.

He vaults over the concrete divider as automatic gunfire rips through the tarmac.

DEV
(into comms, breathing heavily)
Eagle Leader! The package has been hijacked. Target is heading toward the toll junction at 120 clicks!

COMMS OFFICER (V.O.)
Dev, do not engage alone! Backup is six minutes out!

DEV
(reloading with a violent metallic snap)
In six minutes, they cross the border. I'm taking the wheel.`,

  'drama': `SCENE 18: INT. HERITAGE AUDITORIUM STAGE - NIGHT

A single 5000W tungsten spotlight cuts through the airborne dust motes.
MAYA (26) stands in center stage, gripping the worn wooden podium until her knuckles turn white.
In the fifth row of the empty hall, her former mentor, PROFESSOR KESHAV (60), sits in silence.

MAYA
(trembling, but resolute)
For five years, you took credit for the third act of my play. Every review, every national award had your name on it.

KESHAV
(without looking up from his notes)
I gave your words an audience, Maya. Without my signature, they were ink on trash paper.

MAYA
(stepping forward into the blinding light)
Then watch me write the fourth act without you.`,
};

const storyboardShots = [
  {
    id: 1,
    title: 'Shot 01 • Wide Establishing Angle',
    desc: 'Cinematic wide frame capturing atmospheric rain, neon street reflections, and dramatic silhouette depth.',
    camera: 'ARRI Alexa 65 • 35mm Anamorphic f/1.4',
    aspect: '2.39:1 CinemaScope',
    lighting: 'Volumetric Neon Fog & Harsh Backlight',
    image: sb1,
  },
  {
    id: 2,
    title: 'Shot 02 • High-Tension Close-Up',
    desc: 'Intense character framing focusing on micro-expressions, sweat, and eye reflection of headlights.',
    camera: 'RED V-Raptor XL • 85mm T1.5 Prime',
    aspect: '2.39:1 CinemaScope',
    lighting: 'Rembrandt Key Light & Cold Rim Fill',
    image: sb2,
  },
  {
    id: 3,
    title: 'Shot 03 • Dramatic Over-The-Shoulder',
    desc: 'Two-character confrontational depth with shallow focus emphasizing dialogue tension.',
    camera: 'Sony Venice 2 • 50mm Master Anamorphic',
    aspect: '2.39:1 CinemaScope',
    lighting: 'Soft Amber Practical & Neon Spill',
    image: sb3,
  },
];

const talentMatches = [
  {
    name: 'Madhu S',
    role: 'Actor / Action Lead',
    match: 98,
    experience: 'Fresher • Trained in Stunts & Martial Arts',
    language: 'Kannada, Malayalam, English',
    skills: ['Martial Arts', 'Expressive Dialogue', 'Screen Combat'],
    badge: 'Top Compatibility',
  },
  {
    name: 'Rahul K',
    role: 'Character Actor / Antagonist',
    match: 94,
    experience: '4 Years Theatre & Web Series',
    language: 'Kannada, Hindi, English',
    skills: ['Method Acting', 'Vocal Modulation', 'Improvisation'],
    badge: 'High Dramatic Match',
  },
  {
    name: 'Priya V',
    role: 'Leading Lady / Intense Roles',
    match: 91,
    experience: '2 Feature Films • Classical Dancer',
    language: 'Kannada, Tamil, Telugu',
    skills: ['Emotional Range', 'Classical Dance', 'Screen Presence'],
    badge: 'Strong Screen Presence',
  },
];

const FilmhookAIStudioModal = ({ open, onClose }) => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  // Script generator state
  const [selectedGenre, setSelectedGenre] = useState('neo-noir');
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [scriptText, setScriptText] = useState(scriptSamples['neo-noir']);
  const [copied, setCopied] = useState(false);

  // Storyboard state
  const [selectedShotIdx, setSelectedShotIdx] = useState(0);

  // Voice synthesizer state
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [voiceLang, setVoiceLang] = useState('Kannada');
  const [voiceTone, setVoiceTone] = useState('Gritty Detective');

  const handleGenerateScript = () => {
    setIsGeneratingScript(true);
    setScriptText('');
    setTimeout(() => {
      setScriptText(scriptSamples[selectedGenre] || scriptSamples['neo-noir']);
      setIsGeneratingScript(false);
    }, 800);
  };

  const handleCopyScript = () => {
    navigator.clipboard?.writeText(scriptText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleToggleVoice = () => {
    setIsPlayingAudio(prev => !prev);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '26px',
          background: isDarkMode
            ? 'linear-gradient(135deg, #050b17 0%, #030610 100%)'
            : '#ffffff',
          color: isDarkMode ? '#ffffff' : '#0f172a',
          border: isDarkMode
            ? '1.5px solid rgba(6, 182, 212, 0.35)'
            : '1.5px solid rgba(6, 182, 212, 0.25)',
          boxShadow: isDarkMode
            ? '0 30px 80px rgba(0,0,0,0.9), 0 0 50px rgba(6, 182, 212, 0.2)'
            : '0 30px 80px rgba(6, 182, 212, 0.25)',
          overflow: 'hidden',
          maxHeight: '92vh',
        },
      }}
    >
      {/* Top Header Bar */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: isDarkMode
            ? '1px solid rgba(6, 182, 212, 0.2)'
            : '1px solid rgba(6, 182, 212, 0.15)',
          background: isDarkMode
            ? 'rgba(6, 182, 212, 0.05)'
            : 'rgba(6, 182, 212, 0.04)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(6, 182, 212, 0.4)',
            }}
          >
            <AutoAwesomeRoundedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: isDarkMode ? '#fff' : '#0f172a', lineHeight: 1.2 }}>
              Filmhook AI Studio
            </Typography>
            <Typography variant="caption" sx={{ color: '#06b6d4', fontWeight: 800, letterSpacing: '0.04em' }}>
              Advanced Neural Cinema Engine • Live Interactive Playground
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label="Neural Engine v2.4 Active"
            size="small"
            sx={{
              height: 22,
              fontSize: '0.68rem',
              fontWeight: 800,
              bgcolor: 'rgba(16, 185, 129, 0.15)',
              color: '#10b981',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              display: { xs: 'none', sm: 'flex' },
            }}
          />
          <IconButton onClick={onClose} sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Navigation Studio Tabs */}
      <Box sx={{ px: 2, pt: 1.5, borderBottom: isDarkMode ? '1px solid #1e293b' : '1px solid #e2e8f0' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            '& .MuiTabs-indicator': {
              backgroundColor: '#06b6d4',
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab
            icon={<MovieCreationRoundedIcon sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="AI Screenplay Writer"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '0.85rem',
              color: isDarkMode ? '#94a3b8' : '#64748b',
              '&.Mui-selected': { color: '#06b6d4' },
            }}
          />
          <Tab
            icon={<CameraAltRoundedIcon sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Storyboard Visualizer"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '0.85rem',
              color: isDarkMode ? '#94a3b8' : '#64748b',
              '&.Mui-selected': { color: '#06b6d4' },
            }}
          />
          <Tab
            icon={<PersonSearchRoundedIcon sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Smart Casting Radar"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '0.85rem',
              color: isDarkMode ? '#94a3b8' : '#64748b',
              '&.Mui-selected': { color: '#06b6d4' },
            }}
          />
          <Tab
            icon={<MicRoundedIcon sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Voice & Dialogue Lab"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '0.85rem',
              color: isDarkMode ? '#94a3b8' : '#64748b',
              '&.Mui-selected': { color: '#06b6d4' },
            }}
          />
        </Tabs>
      </Box>

      {/* Main Studio Content Body */}
      <DialogContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        {/* ══════════════════════════════════════════════════════════
            MODULE 1: AI SCREENPLAY WRITER
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>
                Select Genre & Generate Hollywood Script Format
              </Typography>
              <Chip
                label="Final Draft Standard"
                size="small"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4' }}
              />
            </Box>

            {/* Genre Selector Pills */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
              {genres.map(g => (
                <Chip
                  key={g.id}
                  label={g.label}
                  onClick={() => {
                    setSelectedGenre(g.id);
                    setScriptText(scriptSamples[g.id] || scriptSamples['neo-noir']);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    bgcolor: selectedGenre === g.id ? '#06b6d4' : (isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
                    color: selectedGenre === g.id ? '#fff' : (isDarkMode ? '#cbd5e1' : '#475569'),
                    border: selectedGenre === g.id ? '1px solid #06b6d4' : '1px solid transparent',
                    '&:hover': {
                      bgcolor: selectedGenre === g.id ? '#0891b2' : (isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'),
                    },
                  }}
                />
              ))}
            </Box>

            {/* Script Action Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Button
                variant="contained"
                onClick={handleGenerateScript}
                disabled={isGeneratingScript}
                startIcon={<RefreshRoundedIcon />}
                size="small"
                sx={{
                  background: 'linear-gradient(90deg, #06b6d4 0%, #3b82f6 100%)',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  textTransform: 'none',
                  borderRadius: '16px',
                  px: 2.5,
                }}
              >
                {isGeneratingScript ? 'AI Writing Scene...' : 'Regenerate Scene Beat'}
              </Button>

              <Button
                variant="outlined"
                onClick={handleCopyScript}
                startIcon={copied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
                size="small"
                sx={{
                  borderRadius: '16px',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  color: copied ? '#10b981' : '#06b6d4',
                  borderColor: copied ? '#10b981' : '#06b6d4',
                }}
              >
                {copied ? 'Copied Script!' : 'Copy Script'}
              </Button>
            </Box>

            {isGeneratingScript && (
              <LinearProgress sx={{ height: 3, borderRadius: 2, mb: 1.5, bgcolor: 'rgba(6, 182, 212, 0.2)', '& .MuiLinearProgress-bar': { bgcolor: '#06b6d4' } }} />
            )}

            {/* Output Screenplay Terminal */}
            <Box
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: '16px',
                background: isDarkMode ? '#030712' : '#f8fafc',
                border: isDarkMode ? '1.5px solid #1e293b' : '1.5px solid #e2e8f0',
                fontFamily: '"Courier New", Courier, monospace',
                fontSize: { xs: '0.8rem', sm: '0.88rem' },
                lineHeight: 1.7,
                color: isDarkMode ? '#e2e8f0' : '#1e293b',
                whiteSpace: 'pre-wrap',
                maxHeight: 280,
                overflowY: 'auto',
                boxShadow: isDarkMode ? 'inset 0 4px 16px rgba(0,0,0,0.6)' : 'inset 0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              {scriptText}
            </Box>

            {/* Live Metrics footer */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2, pt: 1.5, borderTop: isDarkMode ? '1px solid #1e293b' : '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                ⚡ Pacing Score: <strong style={{ color: '#10b981' }}>98/100</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                🎭 Dialogue Authenticity: <strong style={{ color: '#06b6d4' }}>High Emotion</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                🎬 Formatted for: <strong>Final Draft & Industry Production</strong>
              </Typography>
            </Box>
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════════
            MODULE 2: CINEMATIC STORYBOARD VISUALIZER
            ═══════════════════════════════════════════════ */}
        {activeTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>
                Text-to-Cinematic Camera Angles & Lighting
              </Typography>
              <Chip
                label="Anamorphic 2.39:1"
                size="small"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: 'rgba(139, 92, 246, 0.15)', color: '#a855f7' }}
              />
            </Box>

            {/* Shot Navigation Tabs */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
              {storyboardShots.map((shot, idx) => (
                <Button
                  key={shot.id}
                  size="small"
                  variant={selectedShotIdx === idx ? 'contained' : 'outlined'}
                  onClick={() => setSelectedShotIdx(idx)}
                  sx={{
                    borderRadius: '16px',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    textTransform: 'none',
                    background: selectedShotIdx === idx ? 'linear-gradient(90deg, #8b5cf6 0%, #06b6d4 100%)' : 'transparent',
                    borderColor: selectedShotIdx === idx ? 'transparent' : (isDarkMode ? '#334155' : '#cbd5e1'),
                    color: selectedShotIdx === idx ? '#fff' : (isDarkMode ? '#cbd5e1' : '#475569'),
                  }}
                >
                  {shot.title}
                </Button>
              ))}
            </Box>

            {/* Shot Visual Canvas */}
            <Box
              sx={{
                borderRadius: '18px',
                overflow: 'hidden',
                border: isDarkMode ? '1.5px solid rgba(6, 182, 212, 0.3)' : '1.5px solid #e2e8f0',
                background: '#020617',
                position: 'relative',
                boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
              }}
            >
              <Box
                component="img"
                src={storyboardShots[selectedShotIdx].image}
                alt={storyboardShots[selectedShotIdx].title}
                sx={{
                  width: '100%',
                  height: { xs: 200, sm: 260 },
                  objectFit: 'cover',
                  display: 'block',
                }}
              />

              {/* Camera Metadata Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 2,
                  background: 'linear-gradient(to top, rgba(2,6,23,0.95) 0%, rgba(2,6,23,0.6) 60%, transparent 100%)',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <Typography variant="body2" fontWeight={800} sx={{ color: '#fff', fontSize: '0.92rem', mb: 0.5 }}>
                  {storyboardShots[selectedShotIdx].title}
                </Typography>
                <Typography variant="caption" sx={{ color: '#cbd5e1', display: 'block', mb: 1, fontSize: '0.78rem' }}>
                  {storyboardShots[selectedShotIdx].desc}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={storyboardShots[selectedShotIdx].camera}
                    size="small"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(6, 182, 212, 0.2)', color: '#67e8f9' }}
                  />
                  <Chip
                    label={storyboardShots[selectedShotIdx].lighting}
                    size="small"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(139, 92, 246, 0.2)', color: '#c084fc' }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════════
            MODULE 3: SMART CASTING RADAR
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 2 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>
                AI Character-to-Talent Match Breakdown
              </Typography>
              <Chip
                label="Role: Lead Detective"
                size="small"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}
              />
            </Box>

            <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.85rem', mb: 2.5 }}>
              The Filmhook AI engine compares screenplay character requirements against verified talent portfolios, voice recordings, and acting reels:
            </Typography>

            {/* Talent Match Cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {talentMatches.map((talent, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    background: isDarkMode ? '#0a101f' : '#f8fafc',
                    border: isDarkMode
                      ? idx === 0 ? '1.5px solid #10b981' : '1px solid #1e293b'
                      : idx === 0 ? '1.5px solid #10b981' : '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        bgcolor: idx === 0 ? '#10b981' : idx === 1 ? '#06b6d4' : '#8b5cf6',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 900,
                        fontSize: '1rem',
                        flexShrink: 0,
                      }}
                    >
                      {talent.name.substring(0, 2)}
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>
                          {talent.name}
                        </Typography>
                        <Chip
                          label={talent.badge}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.62rem',
                            fontWeight: 800,
                            bgcolor: idx === 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(6, 182, 212, 0.15)',
                            color: idx === 0 ? '#10b981' : '#06b6d4',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', display: 'block', mt: 0.3 }}>
                        {talent.experience} • Langs: {talent.language}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Match Percentage Badge */}
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography
                      variant="h6"
                      fontWeight={900}
                      sx={{
                        color: idx === 0 ? '#10b981' : '#06b6d4',
                        lineHeight: 1,
                        fontSize: '1.2rem',
                      }}
                    >
                      {talent.match}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.65rem', fontWeight: 700 }}>
                      AI Match Score
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════════
            MODULE 4: VOICE & DIALOGUE SYNTHESIZER
            ══════════════════════════════════════════════════════════ */}
        {activeTab === 3 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>
                Multi-Lingual Character Dialogue Synthesizer
              </Typography>
              <Chip
                label="Voice Lab v1.8"
                size="small"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4' }}
              />
            </Box>

            <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.85rem', mb: 2.5 }}>
              Prototype character delivery across multiple Indian and international languages before booking dubbing artists:
            </Typography>

            {/* Language & Tone Selector */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', display: 'block', mb: 1 }}>
                  TARGET LANGUAGE
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['Kannada', 'Malayalam', 'Hindi', 'Tamil', 'English'].map(lang => (
                    <Chip
                      key={lang}
                      label={lang}
                      size="small"
                      onClick={() => setVoiceLang(lang)}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        cursor: 'pointer',
                        bgcolor: voiceLang === lang ? '#06b6d4' : (isDarkMode ? '#1e293b' : '#f1f5f9'),
                        color: voiceLang === lang ? '#fff' : (isDarkMode ? '#cbd5e1' : '#475569'),
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', display: 'block', mb: 1 }}>
                  EMOTIONAL DELIVERY TONE
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['Gritty Detective', 'Fierce Villain', 'Quiet Grief', 'High Comedy'].map(tone => (
                    <Chip
                      key={tone}
                      label={tone}
                      size="small"
                      onClick={() => setVoiceTone(tone)}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        cursor: 'pointer',
                        bgcolor: voiceTone === tone ? '#8b5cf6' : (isDarkMode ? '#1e293b' : '#f1f5f9'),
                        color: voiceTone === tone ? '#fff' : (isDarkMode ? '#cbd5e1' : '#475569'),
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Simulated Audio Player Box */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: '18px',
                background: isDarkMode ? '#030712' : '#f0f9ff',
                border: '1.5px solid rgba(6, 182, 212, 0.35)',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <IconButton
                onClick={handleToggleVoice}
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: '#06b6d4',
                  color: '#fff',
                  boxShadow: '0 6px 18px rgba(6, 182, 212, 0.4)',
                  '&:hover': { bgcolor: '#0891b2' },
                  flexShrink: 0,
                }}
              >
                {isPlayingAudio ? <PauseRoundedIcon sx={{ fontSize: 30 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 30 }} />}
              </IconButton>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>
                    {voiceTone} ({voiceLang})
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDarkMode ? '#67e8f9' : '#0284c7', fontWeight: 800 }}>
                    {isPlayingAudio ? 'Synthesizing Audio Stream...' : 'Ready to Test'}
                  </Typography>
                </Box>

                {/* Animated Waveform Simulation */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', height: 28, py: 0.5 }}>
                  {[24, 12, 28, 16, 20, 10, 26, 14, 22, 30, 18, 12, 26, 15, 29, 20, 14, 25, 11, 28, 19, 13, 27, 22, 10, 26].map((h, i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        height: isPlayingAudio ? `${h}px` : '4px',
                        bgcolor: isPlayingAudio ? '#06b6d4' : (isDarkMode ? '#334155' : '#cbd5e1'),
                        borderRadius: '2px',
                        transition: 'height 0.15s ease',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FilmhookAIStudioModal;
